import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GoogleTranslateService } from 'src/app/services/google-translate.service';

const FILLER: { listening: string[]; thinking: string[]; welcome: string[] } = {
    listening: ['Ok', 'vale', 'ummmm'],
    thinking: ['Vale, entendido... déjame que piense un poco...', 'Interesante.... déjame que lo vea por si puedo ayudarte.', 'Perfecto... dame un segundo que mire para ser mas exactos', 'Lo entiendo, dame un momento que busque bien la información y te digo'],
    welcome: ['Hola, seré tu asistente hoy... cuéntame, te escucho', 'Hola. ¿qué tal?, seré tu asistente de turismo']
};

@Injectable({
    providedIn: 'root'
})
export class LocalFillerPhrasesService {
    private googleTranslate = inject(GoogleTranslateService);
    private lastIndex = -1;
    private isEnabled = true;
    private saidWelcome = false;

    private getPhrases(type: 'listening' | 'thinking' | 'welcome'): string[] {
        console.log(`🗣️ [LocalFiller] Using Spanish filler phrase with user language=${this.getUserLanguage()}`);
        return FILLER[type];
    }

    public sayListeningFiller() {
        this.playFiller(this.getPhrases('listening'), 'listening');
    }

    public sayWelcome() {
        if (this.saidWelcome) return;
        this.playFiller(this.getPhrases('welcome'), 'welcome');
        this.saidWelcome = true;
    }

    public hasSaidWelcome(): boolean {
        return this.saidWelcome;
    }

    public sayThinkingFiller() {
        this.playFiller(this.getPhrases('thinking'), 'thinking');
    }

    /** Reproduce solo el sonido de teclas al iniciar búsqueda/respuesta (sin frase filler). */
    public playTypingSound() {
        if (!this.isEnabled) return;
        const params = `?t=${Date.now()}`;
        const sfxUrl = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3${params}`;
        const sfx = new Audio(sfxUrl);
        sfx.volume = 0.4;
        sfx.play().catch(e => console.warn('⚠️ [LocalFiller] SFX typing error:', e));
    }

    // private playWelcome(){
    //     const welcome = this.getPhrases('welcome');
    //     if (!this.isEnabled || !welcome?.length) 
    //         return;

    //     let index;
    //     do {
    //         index = Math.floor(Math.random() * welcome.length);
    //     } while (index === this.lastIndex && welcome.length > 1);

    //     this.lastIndex = index;
    //     const phrase = welcome[index];

    //     const userLanguage = this.getUserLanguage();
    //     const url = `${environment.apiAssistantUrlBase}/tts/simple?text=${encodeURIComponent(phrase)}&language=${encodeURIComponent(userLanguage)}&t=${Date.now()}`;
    //     const audio = new Audio(url);
    //     audio.play().catch(err => {
    //         console.warn('⚠️ [LocalFiller] Error playing audio filler:', err);
    //     });

    //     console.log(`🗣️ [LocalFiller] Playing (welcome): ${phrase}`);
    // }

    public playWelcome(): Promise<void> {
        const welcome = this.getPhrases('welcome');
        if (!this.isEnabled || !welcome?.length) 
            return Promise.resolve();

        let index: number;
        do {
            index = Math.floor(Math.random() * welcome.length);
        } while (index === this.lastIndex && welcome.length > 1);

        this.lastIndex = index;
        const phrase = welcome[index];

        const userLanguage = this.getUserLanguage();
        const url =
            `${environment.apiAssistantUrlBase}/tts/simple` +
            `?text=${encodeURIComponent(phrase)}` +
            `&language=${encodeURIComponent(userLanguage)}` +
            `&t=${Date.now()}`;

        const audio = new Audio(url);

        return new Promise<void>((resolve, reject) => {
            let settled = false;

            const cleanup = () => {
                audio.removeEventListener("ended", onEnded);
                audio.removeEventListener("error", onError);
                audio.removeEventListener("abort", onError);
                audio.removeEventListener("stalled", onError);
                audio.removeEventListener("emptied", onError);
                if (timeoutId) 
                    clearTimeout(timeoutId);
            };

            const finishOk = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve();
            };

            const finishErr = (err: unknown) => {
                if (settled) 
                    return;

                settled = true;
                cleanup();
                reject(err);
            };

            const onEnded = () => finishOk();
            const onError = () => finishErr(new Error("Audio error / cannot be played"));

            audio.addEventListener("ended", onEnded, { once: true });
            audio.addEventListener("error", onError, { once: true });
            audio.addEventListener("abort", onError, { once: true });
            audio.addEventListener("stalled", onError, { once: true });
            audio.addEventListener("emptied", onError, { once: true });

            // Opcional: corta si no carga/reproduce en X ms
            const timeoutMs = 7500;
            const timeoutId = window.setTimeout(() => {
                finishErr(new Error(`Audio timeout after ${timeoutMs}ms`));
                // por si quieres cortar reproducción:
                try { audio.pause(); } catch {}
            }, timeoutMs);

            // IMPORTANTE: play() devuelve Promise y puede fallar por autoplay policy
            audio.play()
            .then(() => {
                console.log(`🗣️ [LocalFiller] Playing (welcome): ${phrase}`);
            })
            .catch((err) => {
                console.warn("⚠️ [LocalFiller] Error playing audio filler:", err);
                finishErr(err);
            });
        });
    }

    private playFiller(phrases: string[], type: 'listening' | 'thinking' | 'welcome') {
        if (!this.isEnabled || !phrases?.length) return;

        let index;
        do {
            index = Math.floor(Math.random() * phrases.length);
        } while (index === this.lastIndex && phrases.length > 1);

        this.lastIndex = index;
        const phrase = phrases[index];

        const userLanguage = this.getUserLanguage();
        const url = `${environment.apiAssistantUrlBase}/tts/simple?text=${encodeURIComponent(phrase)}&language=${encodeURIComponent(userLanguage)}&t=${Date.now()}`;
        const audio = new Audio(url);

        audio.play().catch(err => {
            console.warn('⚠️ [LocalFiller] Error playing audio filler:', err);
        });

        if (type === 'thinking') {
            audio.onended = () => {
                const params = `?t=${Date.now()}`;
                const sfxUrl = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3${params}`;
                const sfx = new Audio(sfxUrl);
                sfx.volume = 0.4;
                sfx.play().catch(e => console.warn('⚠️ [LocalFiller] SFX error:', e));
            };
        }

        console.log(`🗣️ [LocalFiller] Playing (${type}): ${phrase}`);
    }

    public setEnabled(enabled: boolean) {
        this.isEnabled = enabled;
    }

    private getUserLanguage(): string {
        const { value: cookieLang, source } = this.getLanguageFromCookie();
        const rawLang = cookieLang || this.getBrowserLanguage();
        const resolvedLang = this.normalizeLang(rawLang);
        console.log(`🗣️ [LocalFiller] Resolved user language: ${resolvedLang} (raw=${rawLang}, source=${source || 'browser'})`);
        return resolvedLang;
    }

    private normalizeLang(lang: string): string {
        let decoded = lang;
        try {
            decoded = decodeURIComponent(lang);
        } catch {
            decoded = lang;
        }
        decoded = decoded.trim().replace(/^"+|"+$/g, '');
        const fromPath = decoded.includes('/') ? decoded.split('/').filter(Boolean).pop() || decoded : decoded;
        const normalized = fromPath.toLowerCase().split(/[-_]/)[0];
        return normalized || 'es';
    }

    private getBrowserLanguage(): string {
        if (typeof navigator === 'undefined') return 'es';
        const rawLang = navigator.languages?.[0] || navigator.language || 'es';
        return rawLang.toLowerCase();
    }

    private getLanguageFromCookie(): { value: string | null; source: string | null } {
        const localLang = this.googleTranslate.getLocalLang();
        if (localLang)
            return { value: localLang, source: 'locallang' };

        const googtransLang = this.googleTranslate.getCookieLang();
        if (googtransLang)
            return { value: googtransLang, source: 'googtrans' };

        const cookies = this.readCookies();
        const directCookieLang = cookies['lang']
            || cookies['language']
            || cookies['locale']
            || cookies['selectedLanguage']
            || cookies['i18next']
            || cookies['NEXT_LOCALE'];

        if (directCookieLang)
            return { value: directCookieLang, source: 'generic-cookie' };

        return { value: null, source: null };
    }

    private readCookies(): Record<string, string> {
        if (typeof document === 'undefined')
            return {};

        return document.cookie
            .split(';')
            .map(chunk => chunk.trim())
            .filter(Boolean)
            .reduce((acc, chunk) => {
                const separatorIndex = chunk.indexOf('=');
                if (separatorIndex < 0)
                    return acc;

                const key = chunk.slice(0, separatorIndex).trim();
                const value = chunk.slice(separatorIndex + 1).trim();
                try {
                    acc[key] = decodeURIComponent(value);
                } catch {
                    acc[key] = value;
                }
                return acc;
            }, {} as Record<string, string>);
    }
}
